/**
 * useLogin Hook
 * Custom hook for handling login logic with role-based auto-routing
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setAuthenticated, fetchUserPermissions } from '../redux/features/adminSlice';
import customAuthService, { mockAuthService } from '../services/customAuthService';

/**
 * Custom hook for login functionality
 * @param {Object} options - Configuration options
 * @param {boolean} options.useMock - Use mock service for development (default: false)
 * @returns {Object} Login state and methods
 */
export const useLogin = (options = {}) => {
  const { useMock = false } = options;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Login with email and password
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {boolean} rememberMe - Request a long-lived session on this device. The
   *   backend decides whether it is actually granted and returns `rememberMeApplied`
   *   in the response — we simply forward the user's choice.
   * @returns {Promise<Object>} Login response (includes rememberMeApplied / sessionTtlSeconds)
   */
  const login = async (email, password, rememberMe = false) => {
    setLoading(true);
    setError('');

    try {
      // Validate inputs
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // Call auth service
      const response = useMock
        ? await mockAuthService.login(email, password, rememberMe)
        : await customAuthService.login(email, password, rememberMe);

      if (response.success) {
        // Update Redux state with user data
        // setAuthenticated expects user data as payload
        dispatch(setAuthenticated(response.user));

        // Store user data in localStorage for persistence (optional)
        localStorage.setItem('user', JSON.stringify(response.user));

        // Fetch full feature permissions + account into Redux so the app
        // (e.g. Seller sidebar INTEGRATIONS section) is permission-aware
        // immediately after login — not only after a fresh protected-route load.
        // Uses the session cookie just set by /api/auth/login (via GET /api/auth/status).
        try {
          await dispatch(fetchUserPermissions()).unwrap();
        } catch (permErr) {
          console.error('[useLogin] fetchUserPermissions after login failed:', permErr);
        }

        // Auto-route based on redirect_url from backend
        navigate(response.redirect_url);

        return response;
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    setError('');
  };

  return {
    login,
    loading,
    error,
    clearError,
  };
};

export default useLogin;